/** Prequalification questions wizard — walks STEPS array before Auth0 redirect. */

import * as Wizard from '#~/agent/components/wizard/index.js';
import { Sel, STagNew } from '#~/stag/index.js';
import { Anchor } from '@studio/design-system/button';
import { Button } from '@studio/design-system/button';
import type { ReactElement, ReactNode } from 'react';
import { useCallback, useState } from 'react';
import { twMerge } from 'cn';
import type { PrequalificationAnswers } from './prequal-storage.js';
import {
  STEPS,
  TOTAL_STEPS,
  deriveStartStep,
  isPrequalComplete,
  savePrequalResponses
} from './prequal-storage.js';

// ─── Component ───

interface Props {
  onComplete: (responses: PrequalificationAnswers) => void;
  initialResponses?: Partial<PrequalificationAnswers>;
  /**
   * Escape-hatch control, forwarded to the wizard's persistent chrome
   * (`Wizard.secondaryAction`) rather than rendered by the caller.
   *
   * Keep the escape action outside the scrolling card so long question steps
   * cannot push it below the content. Very short windows scroll the whole page.
   */
  footer?: ReactNode;
}

function pickInitialStep(initial: Partial<PrequalificationAnswers> | undefined): number {
  const derived = deriveStartStep(initial);
  return derived >= TOTAL_STEPS ? 0 : derived;
}

export const PrequalFlow = ({ onComplete, initialResponses, footer }: Props): ReactElement => {
  const [step, setStep] = useState<number>(() => pickInitialStep(initialResponses));
  const [responses, setResponses] = useState<Partial<PrequalificationAnswers>>(
    () => initialResponses ?? {}
  );

  const currentStep = STEPS[step];

  const handleSelect = useCallback((field: keyof PrequalificationAnswers, value: string): void => {
    setResponses((prev) => {
      const next = { ...prev, [field]: value };
      savePrequalResponses(next);
      return next;
    });
  }, []);

  const handleContinue = useCallback((): void => {
    const next = step + 1;
    if (next >= TOTAL_STEPS) {
      if (isPrequalComplete(responses)) {
        onComplete(responses);
      }
      return;
    }
    setStep(next);
  }, [step, responses, onComplete]);

  const handleBack = useCallback((): void => {
    if (step > 0) setStep(step - 1);
  }, [step]);

  if (currentStep === undefined) return <></>;

  const currentValue = responses[currentStep.field];
  const isCurrentAnswerValid = currentStep.options.some((opt) => opt.value === currentValue);

  return (
    // Keep the escape action visible while long question lists scroll.
    <Wizard.Root
      scrollPage={false}
      currentStep={step + 1}
      totalSteps={TOTAL_STEPS}
      {...(footer !== undefined ? { secondaryAction: footer } : {})}
    >
      <Wizard.Verbiage message={currentStep.subtitle} title={currentStep.title} />

      <div className='flex w-full flex-col gap-10 px-16'>
        {currentStep.options.map((option) => {
          const isSelected = currentValue === option.value;
          return (
            <Button
              key={option.value}
              className={twMerge('w-full', isSelected ? '' : 'border-base-200')}
              size='xl'
              variant={isSelected ? 'primary' : 'secondary'}
              onClick={() => {
                handleSelect(currentStep.field, option.value);
              }}
              // Per rule: playwright-stag-selectors-only (ai.knowledge/platform) —
              // option value as the unique id so the e2e can pick a specific answer.
              {...STagNew(Sel.Prequalification, 'option', option.value)}
            >
              {option.label}
            </Button>
          );
        })}
      </div>

      <Wizard.ButtonBox className='w-full px-16'>
        <Wizard.Button
          className='w-full'
          disabled={!isCurrentAnswerValid}
          onClick={handleContinue}
          {...STagNew(Sel.Prequalification, 'continue')}
        >
          Continue
        </Wizard.Button>

        {step > 0 && (
          <Anchor className='text-primary' onClick={handleBack}>
            Back
          </Anchor>
        )}
      </Wizard.ButtonBox>
    </Wizard.Root>
  );
};
